import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { 
  MoreVertical, Trash2, Calendar, MapPin, Clock, Users, 
  Check, ChevronDown, ChevronUp, Loader2
} from "lucide-react";
import { getGroupEventAttendees, resolveImageUrl } from "@/lib/api";

export default function EventCard({ event, groupId, isAdmin, onDelete, onRsvp }) {
  const [showAttendees, setShowAttendees] = useState(false);
  const [attendees, setAttendees] = useState([]);
  const [loadingAttendees, setLoadingAttendees] = useState(false);

  const loadAttendees = async () => {
    setLoadingAttendees(true);
    try {
      const res = await getGroupEventAttendees(groupId, event.event_id);
      setAttendees(res.data.attendees || []);
    } catch {
      toast.error("Failed to load attendees");
    }
    setLoadingAttendees(false);
  };

  const handleToggleAttendees = () => {
    if (!showAttendees) {
      loadAttendees();
    }
    setShowAttendees(!showAttendees);
  };

  const formatEventDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isPastEvent = new Date(event.event_date) < new Date();

  return (
    <Card className={`bg-white border-[#E5E0D5] ${isPastEvent ? 'opacity-60' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h4 className="font-['Crimson_Text'] font-semibold text-[#2D2A26] text-lg">{event.title}</h4>
            <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">{event.description}</p>
            
            <div className="flex flex-wrap gap-3 mt-3">
              <span className="flex items-center gap-1 text-sm text-[#6B665E] font-['Nunito']">
                <Calendar className="w-4 h-4" />
                {formatEventDate(event.event_date)}
              </span>
              {event.event_time && (
                <span className="flex items-center gap-1 text-sm text-[#6B665E] font-['Nunito']">
                  <Clock className="w-4 h-4" />
                  {event.event_time}
                </span>
              )}
              {event.location && (
                <span className="flex items-center gap-1 text-sm text-[#6B665E] font-['Nunito']">
                  <MapPin className="w-4 h-4" />
                  {event.location}
                </span>
              )}
            </div>
          </div>

          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white">
                <DropdownMenuItem onClick={() => onDelete(event.event_id)} className="text-red-600">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Event
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="flex items-center gap-3 mt-4 pt-3 border-t border-[#E5E0D5]">
          <Button
            variant={event.user_rsvp ? "default" : "outline"}
            size="sm"
            onClick={() => onRsvp(event.event_id)}
            disabled={isPastEvent}
            className={event.user_rsvp ? "bg-[#556B2F] hover:bg-[#445624]" : "border-[#E5E0D5]"}
            data-testid={`rsvp-event-${event.event_id}`}
          >
            {event.user_rsvp ? <Check className="w-4 h-4 mr-1" /> : null}
            {event.user_rsvp ? "Going" : "RSVP"}
          </Button>
          
          <button 
            onClick={handleToggleAttendees}
            className="text-sm text-[#6B665E] hover:text-[#2D2A26] font-['Nunito'] flex items-center gap-1"
          >
            <Users className="w-4 h-4" />
            {event.rsvp_count || 0} attending
            {showAttendees ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>

        {showAttendees && (
          <div className="mt-3 pt-3 border-t border-[#E5E0D5]">
            {loadingAttendees ? (
              <div className="flex justify-center py-2">
                <Loader2 className="w-4 h-4 animate-spin text-[var(--liturgical-primary)]" />
              </div>
            ) : attendees.length === 0 ? (
              <p className="text-sm text-[#6B665E] font-['Nunito'] text-center">No attendees yet</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {attendees.map(attendee => (
                  <div key={attendee.user_id} className="flex items-center gap-1 bg-[#F9F7F2] rounded-full px-2 py-1">
                    <Avatar className="h-5 w-5">
                      <AvatarImage src={resolveImageUrl(attendee.picture)} />
                      <AvatarFallback className="text-[8px] bg-[var(--liturgical-primary)] text-white">
                        {attendee.name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs font-['Nunito'] text-[#2D2A26]">{attendee.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
